import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tag, Check, X, Loader } from 'lucide-react';

interface PromoCodeInputProps {
  promoCode: string;
  isValidating: boolean;
  isValid: boolean | null;
  discountAmount: number;
  validationMessage: string;
  onPromoCodeChange: (code: string) => void;
  onValidate: () => void;
  onRemove: () => void;
  enableDarkMode?: boolean; // Active le mode nuit (pour le côté professionnel)
}

const PromoCodeInput: React.FC<PromoCodeInputProps> = ({
  promoCode,
  isValidating,
  isValid,
  discountAmount,
  validationMessage,
  onPromoCodeChange,
  onValidate,
  onRemove,
  enableDarkMode = false,
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className={`bg-white rounded-lg border border-gray-200 overflow-hidden ${enableDarkMode ? 'dark:bg-gray-800 dark:border-gray-700' : ''}`}>
      {/* Header - toujours visible */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className={`w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors ${enableDarkMode ? 'dark:hover:bg-gray-700' : ''}`}
      >
        <div className="flex items-center gap-2">
          <Tag size={20} className={`text-purple-600 ${enableDarkMode ? 'dark:text-purple-400' : ''}`} />
          <span className={`font-medium text-gray-900 ${enableDarkMode ? 'dark:text-gray-100' : ''}`}>
            {t('booking.promoCode', 'Code promo')}
          </span>
          {isValid && discountAmount > 0 && (
            <span className={`ml-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium ${enableDarkMode ? 'dark:bg-green-900/30 dark:text-green-400' : ''}`}>
              -{discountAmount.toFixed(2)}€
            </span>
          )}
        </div>
        <div className={`text-purple-600 ${enableDarkMode ? 'dark:text-purple-400' : ''}`}>
          {isExpanded ? '−' : '+'}
        </div>
      </button>

      {/* Contenu expansible */}
      {isExpanded && (
        <div className={`p-4 pt-3 border-t border-gray-100 ${enableDarkMode ? 'dark:border-gray-700' : ''}`}>
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <input
                type="text"
                value={promoCode}
                onChange={(e) => onPromoCodeChange(e.target.value.toUpperCase())}
                placeholder={t('booking.enterPromoCode', 'Entrez votre code promo') as string}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent uppercase bg-white text-gray-900 placeholder-gray-400 ${enableDarkMode ? 'dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-500' : ''}`}
                disabled={isValid === true}
              />
              {isValid === true && (
                <div className="absolute right-2 top-1/2 -translate-y-1/2">
                  <Check size={20} className={`text-green-600 ${enableDarkMode ? 'dark:text-green-400' : ''}`} />
                </div>
              )}
              {isValid === false && (
                <div className="absolute right-2 top-1/2 -translate-y-1/2">
                  <X size={20} className={`text-red-600 ${enableDarkMode ? 'dark:text-red-400' : ''}`} />
                </div>
              )}
            </div>

            {isValid !== true ? (
              <button
                type="button"
                onClick={onValidate}
                disabled={!promoCode || isValidating}
                className={`px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 ${enableDarkMode ? 'dark:bg-purple-500 dark:hover:bg-purple-600' : ''}`}
              >
                {isValidating ? (
                  <>
                    <Loader size={18} className="animate-spin" />
                    <span>{t('booking.validating', 'Validation...')}</span>
                  </>
                ) : (
                  <span>{t('booking.apply', 'Appliquer')}</span>
                )}
              </button>
            ) : (
              <button
                type="button"
                onClick={onRemove}
                className={`px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors ${enableDarkMode ? 'dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700' : ''}`}
              >
                {t('booking.remove', 'Retirer')}
              </button>
            )}
          </div>

          {/* Message de validation */}
          {validationMessage && (
            <div className={`mt-2 text-sm ${isValid ? `text-green-600 ${enableDarkMode ? 'dark:text-green-400' : ''}` : `text-red-600 ${enableDarkMode ? 'dark:text-red-400' : ''}`}`}>
              {validationMessage}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PromoCodeInput;
